/* tslint:disable:max-file-line-count */
import * as React from 'react';
import { IObservableArray } from 'mobx';
import * as Swipeable from 'react-swipeable';

import { ClickTracker } from 'bernie-analytics';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { ImageViewModel } from 'models';
import { KeyCodes } from 'constants/key-codes.ts';

import { Level, liveAnnounce } from 'uitk-react-utils';
import { UitkFigure, UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkPagingNextButton, UitkPagingPrevButton } from 'uitk-react-button';

import { QueuedImage } from './queued-image';
import { serverRenderImageFallbackcb } from 'components/search/listing/internals/title-image';

const ButtonNextTracked = ClickTracker(UitkPagingNextButton);
const ButtonPrevTracked = ClickTracker(UitkPagingPrevButton);

interface GallerySwipeProps {
  currentIndex?: number;
  enablePreload?: boolean;
  galleryName?: string;
  images: IObservableArray<ImageViewModel> | ImageViewModel[];
  onImageClick?: () => void;
  onIndexChange?: (index: number) => void;
  hideImgNumber?: boolean;
  hideCaption?: boolean;
  sendImgIndexInAnalytics?: boolean;
  ratio?: UitkFigureAspectRatioType;
  enableKeyboardNavigation?: boolean;
  noScrim?: boolean;
}

type LocalizedGallerySwipeProps = LocalizedComponentProps & GallerySwipeProps;

interface GallerySwipeState {
  currentIndex: number;
  enablePreload: boolean;
  isAnimating?: boolean;
  isAnimatingNext?: boolean;
  isAnimatingPrev?: boolean;
  isInteractive: boolean;
  nextImageId: string;
  prevImageId: string;
}

@withLocalization
export class GallerySwipe extends React.Component<LocalizedGallerySwipeProps, GallerySwipeState> {
  constructor(props) {
    super(props);

    this.state = {
      currentIndex: this.props.currentIndex,
      enablePreload: false,
      isInteractive: false,
      nextImageId: '0',
      prevImageId: '0'
    };

    this.onImageClickCallback = this.onImageClickCallback.bind(this);
    this.onNextCallback = this.onNextCallback.bind(this);
    this.onPrevCallback = this.onPrevCallback.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  public static defaultProps: Partial<LocalizedGallerySwipeProps> = {
    currentIndex: 0,
    enablePreload: true,
    ratio: UitkFigureAspectRatioType.R16_9
  };

  private _animationTimer: any;

  // getter / setter for this.currentIndex
  get currentIndex() {
    return this.state.currentIndex;
  }

  set currentIndex(value) {
    this.setState({ currentIndex: value });
  }

  private onNextCallback(): void {
    this.handleNavigate(true);
  }

  private onPrevCallback(): void {
    this.handleNavigate(false);
  }

  private handleNavigate(isNext: boolean): void {
    if (this.state.isAnimating) {
      return;
    }

    this.setState({
      enablePreload: true,
      isAnimating: true,
      isAnimatingNext: isNext,
      isAnimatingPrev: !isNext
    });

    if (this._animationTimer) {
      clearInterval(this._animationTimer);
    }

    this._animationTimer = setTimeout(() => {
      const index = isNext ? this.getForwardIndex(1) : this.getBackwardIndex(1);
      this.announce(index);

      const onIndexChange = this.props.onIndexChange;
      if (typeof onIndexChange === 'function') {
        onIndexChange(index);
      }
    }, 300);
  }

  private onImageClickCallback(e): void {
    e.preventDefault();
    const onImageClick = this.props.onImageClick;

    if (typeof onImageClick === 'function') {
      onImageClick();
    }
  }

  // get a valid index within min/max bounds, regardless of count
  private getForwardIndex(count): number {
    const images = this.props.images;

    if (images && images.length > 0 && count > 0) {
      const softIndex = this.currentIndex + count;

      return Math.abs(softIndex % images.length);
    }

    return this.currentIndex;
  }

  private getBackwardIndex(count): number {
    const images = this.props.images;

    if (images && images.length > 0 && count > 0) {
      if (images.length === 1) {
        return 0;
      }

      const softIndex = this.currentIndex - count;

      if (softIndex >= 0) {
        return softIndex;
      }

      if (Math.abs(softIndex % images.length) === 0) {
        return 0;
      }

      return Math.abs(images.length - Math.abs(softIndex % images.length));
    }
  }

  private announce(index): void {
    const { images, l10n } = this.props;
    const { formatText } = l10n;

    this.setState(
      {
        currentIndex: index,
        isAnimating: false,
        isAnimatingNext: false,
        isAnimatingPrev: false
      },
      () => {
        const { currentIndex } = this.state;

        liveAnnounce(
          formatText('media.gallery.liveAnnounce', currentIndex + 1, images.length, this.getImage(currentIndex).caption),
          Level.POLITE
        );
      }
    );
  }

  private getImage(index): ImageViewModel {
    const images = this.props.images;

    if (images && images.length > 0 && index >= 0 && index < images.length) {
      return images[index];
    }

    return { src: '', caption: '' };
  }

  private handleKeyDown(e): void {
    if (e.keyCode === KeyCodes.RIGHTARROW) {
      this.onNextCallback();
    } else if (e.keyCode === KeyCodes.LEFTARROW) {
      this.onPrevCallback();
    }
  }

  public componentWillMount(): void {
    this.setState({
      nextImageId: `nextImage-${Math.round(Math.random() * 10000)}`,
      prevImageId: `prevImage-${Math.round(Math.random() * 10000)}`
    });
  }

  public componentDidMount(): void {
    this.setState({
      enablePreload: this.props.enablePreload,
      isAnimating: false,
      isInteractive: true
    });

    if (this.props.enableKeyboardNavigation) {
      window.addEventListener('keydown', this.handleKeyDown);
    }
  }

  public componentWillUnmount() {
    if (this.props.enableKeyboardNavigation) {
      window.removeEventListener('keydown', this.handleKeyDown);
    }
  }

  public componentWillReceiveProps(nextProps): void {
    const nextCurrIndex = nextProps.currentIndex;

    if (nextCurrIndex !== this.state.currentIndex) {
      this.setState({ currentIndex: nextCurrIndex });
    }
  }

  public shouldComponentUpdate(nextProps, nextState): boolean {
    const { currentIndex, images } = this.props;
    const initialFirstImage = images && images.length > 0 && images[0] && images[0].src;
    const nextFirstImage = nextProps && nextProps.images && nextProps.images.length > 0 && nextProps.images[0] && nextProps.images[0].src;
    const nextImages = nextProps.images;

    // only re-render when images, isAnimating or currentIndex change
    const imageLength = images && images.length;
    const nextImageLength = nextImages && nextImages.length;
    if (imageLength !== nextImageLength) {
      return true;
    }
    if (this.state.isAnimating !== nextState.isAnimating) {
      return true;
    }
    if (currentIndex !== nextProps.currentIndex) {
      return true;
    }
    if (initialFirstImage !== nextFirstImage) {
      return true;
    }

    return false;
  }

  public render(): JSX.Element {
    const { galleryName, images, l10n, hideImgNumber, hideCaption, sendImgIndexInAnalytics, ratio, noScrim } = this.props;
    const {
      currentIndex,
      enablePreload,
      isAnimating,
      isAnimatingNext,
      isAnimatingPrev,
      isInteractive,
      nextImageId,
      prevImageId
    } = this.state;

    const { formatText } = l10n;

    // index displayed in UI, e.g. 1 / 40
    const currentIndexDisplay: number = this.currentIndex + 1;
    const canSwipe: boolean = isInteractive && (images && images.length > 1);
    let figureAnimationClass: string = '';

    if (isAnimating) {
      if (isAnimatingPrev) {
        figureAnimationClass = 'photo-gallery__figure--animating-prev';
      } else if (isAnimatingNext) {
        figureAnimationClass = 'photo-gallery__figure--animating-next';
      }
    }

    const currentImage: ImageViewModel = this.getImage(currentIndex);

    const prevImageUrl: string = enablePreload ? this.getImage(this.getBackwardIndex(1)).src : '';
    const currentImageUrl: string = currentImage.src;
    const currentImageFallbackUrl: string = currentImage.fallBackImage;
    const nextImageUrl: string = enablePreload ? this.getImage(this.getForwardIndex(1)).src : '';
    const nextNextImageUrl: string = enablePreload ? this.getImage(this.getForwardIndex(2)).src : '';

    const imgCaption: string = currentImage.caption;
    const photoGalleryCaptionClasses = [
      'photo-gallery__caption',
      'all-y-padding-three',
      'all-x-padding-three',
      'uitk-type-300',
      'uitk-type-medium'
    ];
    const swipeImageClasses = 'photo-gallery__image photo-gallery__image--active image-loader';
    const scrimClass = noScrim ? ' image-loader__no-scrim' : '';
    const galleryClasses = 'photo-gallery photo-gallery--swipe transparent--nav';

    const serverRenderImageFallbackStylecb = currentImageFallbackUrl
      ? (style: object) => serverRenderImageFallbackcb(currentImageFallbackUrl, style)
      : null;

    // We use keys on the QueuedImages to prevent React from recycling them and
    // screwing up the image loading
    return (
      <div className={galleryClasses}>
        {images &&
          images.length > 0 && (
            <UitkFigure className={`photo-gallery__figure ${figureAnimationClass}`} ratio={ratio}>
              {prevImageUrl &&
                prevImageUrl !== '' && (
                  <QueuedImage
                    className={`photo-gallery__image photo-gallery__image--prev image-loader${scrimClass}`}
                    key={`prev1_${prevImageUrl}`}
                    src={prevImageUrl}
                    useBackgroundImage={true}
                  />
                )}

              <Viewport>
                <ViewSmall>
                  <Swipeable
                    data-stid="gallery-image-trigger"
                    onSwipedLeft={canSwipe ? this.onNextCallback : undefined}
                    onSwipedRight={canSwipe ? this.onPrevCallback : undefined}
                    onClick={this.onImageClickCallback}
                    tabIndex="0"
                  >
                    <QueuedImage
                      className={`${swipeImageClasses}${scrimClass} float-above-card-link`}
                      key={currentImageUrl}
                      serverRender={true}
                      serverRenderCallback={serverRenderImageFallbackStylecb}
                      src={currentImageUrl}
                      useBackgroundImage={true}
                    />
                  </Swipeable>
                </ViewSmall>
                <ViewLarge>
                  <div data-stid="gallery-image-trigger" onClick={this.onImageClickCallback}>
                    <QueuedImage
                      className={`${swipeImageClasses}${scrimClass}`}
                      key={currentImageUrl}
                      serverRender={true}
                      serverRenderCallback={serverRenderImageFallbackStylecb}
                      src={currentImageUrl}
                      useBackgroundImage={true}
                    />
                  </div>
                </ViewLarge>
              </Viewport>

              {nextImageUrl &&
                nextImageUrl !== '' && (
                  <QueuedImage
                    className={`photo-gallery__image photo-gallery__image--next image-loader${scrimClass}`}
                    key={`next1_${nextImageUrl}`}
                    src={nextImageUrl}
                    useBackgroundImage={true}
                  />
                )}

              {nextNextImageUrl &&
                nextNextImageUrl !== '' && (
                  <QueuedImage
                    className={`photo-gallery__image photo-gallery__image--queued image-loader${scrimClass}`}
                    key={`next2_${nextNextImageUrl}`}
                    src={nextNextImageUrl}
                    useBackgroundImage={true}
                  />
                )}
            </UitkFigure>
          )}

        {images &&
          isInteractive && (
            <>
              {images.length > 1 && (
                <span className="photo-gallery__nav photo-gallery__nav--prev all-x-padding-two all-y-padding-two float-above-card-link">
                  <ButtonPrevTracked
                    iconId={prevImageId}
                    iconTitle={formatText('media.gallery.previous.icon.title')}
                    linkName="Previous Image"
                    onClick={this.onPrevCallback}
                    rfrr={sendImgIndexInAnalytics ? `Photos.Left.${currentIndex}` : `.${galleryName}.PhotoGallery.ClickLeft`}
                  />
                </span>
              )}

              {images.length > 0 &&
                !hideImgNumber && (
                  <span className={photoGalleryCaptionClasses.join(' ')}>
                    {`${currentIndexDisplay} / ${images.length}`} {!hideCaption && imgCaption && imgCaption !== '' && `- ${imgCaption}`}
                  </span>
                )}

              {images.length > 1 && (
                <span className="photo-gallery__nav photo-gallery__nav--next all-x-padding-two all-y-padding-two float-above-card-link">
                  <ButtonNextTracked
                    iconId={nextImageId}
                    iconTitle={formatText('media.gallery.next.icon.title')}
                    linkName="Next Image"
                    onClick={this.onNextCallback}
                    rfrr={sendImgIndexInAnalytics ? `Photos.Right.${currentIndex}` : `.${galleryName}.PhotoGallery.ClickRight`}
                  />
                </span>
              )}
            </>
          )}
      </div>
    );
  }
}
